import { gql, useMutation } from '@apollo/client'
import { Button, useToast } from '@farol-ds/react'
import { useCallback, useContext } from 'react'

import { useRegisterLockEvent } from '../../hooks'
import { BoxLockContext } from '../context'
import { SubscribeButton, SubscribeButtonProps } from '../subscribe-button'
import {
  Pipedrive4BizButton_OfferLockCardFragment,
  Pipedrive4BizMutation,
  Pipedrive4BizMutationVariables,
} from './__generated__/pipedrive-4-biz-button.graphql'

export type Pipedrive4BizButtonProps = {
  kind?: SubscribeButtonProps['kind']
  offerLockCard: Pipedrive4BizButton_OfferLockCardFragment
}

export const PIPEDRIVE_4_BIZ_MUTATION = gql`
  mutation pipedrive4Biz($input: AddPipedrive4bizDealInput!) {
    addPipedrive4bizDeal(input: $input) {
      success
    }
  }
`

export function Pipedrive4BizButton({ kind = 'primary', offerLockCard }: Pipedrive4BizButtonProps) {
  const toast = useToast()

  const { interactive, setStatus } = useContext(BoxLockContext)
  const { registerLockEvent } = useRegisterLockEvent()
  const [pipedrive4Biz] = useMutation<Pipedrive4BizMutation, Pipedrive4BizMutationVariables>(
    PIPEDRIVE_4_BIZ_MUTATION
  )

  const {
    buttons: [button],
  } = offerLockCard
  const { url, label } = button || {}

  const showToast = useCallback(
    (status: 'success' | 'error') => {
      const toastMessage = {
        success: 'Solicitação enviada com sucesso!',
        error: 'Erro ao enviar solicitação',
      }[status]

      return toast(toastMessage, {
        type: status === 'success' ? 'neutral' : 'negative',
      })
    },
    [toast]
  )

  const registerEvent = useCallback(() => {
    registerLockEvent({
      eventProperties: [offerLockCard.lockEventProperties],
    })
  }, [offerLockCard.lockEventProperties, registerLockEvent])

  const onClick = useCallback(async () => {
    if (!interactive) return

    setStatus('processing')

    registerEvent()

    try {
      const fetchResult = await pipedrive4Biz({
        variables: { input: { source: offerLockCard.lockEventProperties.from_component } },
      })
      const newStatus = fetchResult.data?.addPipedrive4bizDeal?.success ? 'success' : 'error'
      setStatus(newStatus)
      showToast(newStatus)
    } catch (err) {
      console.error('[shared-box-lock] Pipedrive4BizMutation', err)
      showToast('error')
      setStatus('error')
    }
  }, [
    interactive,
    offerLockCard?.lockEventProperties,
    pipedrive4Biz,
    registerEvent,
    setStatus,
    showToast,
  ])

  return url ? (
    <Button block kind={kind} onClick={registerEvent} asChild>
      <a href={url} target="_blank" rel="noreferrer">
        {label}
      </a>
    </Button>
  ) : (
    <SubscribeButton offerLockCard={offerLockCard} onClick={onClick} kind={kind} />
  )
}

Pipedrive4BizButton.fragments = {
  offerLockCard: gql`
    fragment Pipedrive4BizButton_offerLockCard on OfferLockCard {
      lockEventProperties {
        pid
        feature
        current_plan
        next_plan
        plan_price
        modal_type
        experiment_id
        app_id
        from_component
        resource_id
        parent_resource_id
        is_upgrade
        is_one_click
      }
      buttons {
        url
        label
      }
      ...SubscribeButton_offerLockCard
    }

    ${SubscribeButton.fragments.offerLockCard}
  `,
}
